import React from 'react';
import type { User as ApiUser } from '../types';
import type { User as RoleUser } from '../utils/roleUtils';

export type AuthUser = ApiUser & Partial<RoleUser>;

export type AuthPhase =
  | 'loading'
  | 'unauthenticated'
  | 'terms_acceptance'
  | 'onboarding'
  | 'subscription'
  | 'billing_recovery'
  | 'intake'
  | 'b2b_plan_selection'
  | 'school_inactive'
  | 'authenticated';

export interface BillingStatus {
  plan_tier?: string | null;
  plan_type?: string | null;
  subscription_status?: string | null;
  terms_accepted?: boolean | null;
  onboarding_completed?: boolean | null;
  intake_completed?: boolean | null;
  current_period_end?: number | null;
  /** Unix time until which the EU withdrawal function is offered (BIL-L23), or null. */
  withdrawal_available_until?: number | null;
  cancel_at_period_end?: boolean | null;
  cancel_at?: string | null;
  pending_plan_tier?: string | null;
  pending_billing_cycle?: string | null;
  pending_plan_effective_at?: string | null;
  free_trial_eligible?: boolean;
  free_trial_active?: boolean;
  free_trial_expired?: boolean;
  free_trial_session_id?: number | null;
  free_trial_kind?: 'legacy_no_card' | 'card_backed' | null;
  free_trial_grandfathered?: boolean;
  has_purchased_credits?: boolean;
  course_access_active?: boolean;
  course_access_until?: string | null;
  /** Server-decided feature access (backend `entitlements.can`). Read via `hasEntitlement`. */
  entitlements?: Partial<Record<EntitlementFeature, boolean>>;
}

/** Mirrors `Feature` in backend/app/services/entitlements.py. */
export type EntitlementFeature =
  | 'paid_access'
  | 'analyze'
  | 'voice_transcribe'
  | 'view_own_reports'
  | 'buy_credits'
  | 'chat'
  | 'chat_reassess'
  | 'icf_progress'
  | 'practice_exam'
  | 'mcs_prep'
  | 'mentor_engagements'
  | 'mentor_training';

export type RefreshAuthOptions = {
  forceBillingRefresh?: boolean;
  forceUserRefresh?: boolean;
};

export interface AuthContextValue {
  phase: AuthPhase;
  user: AuthUser | null;
  billingStatus: BillingStatus | null;
  isLoading: boolean;
  refreshAuth: (options?: RefreshAuthOptions) => Promise<void>;
  logout: () => Promise<void>;
  setPhase: (phase: AuthPhase) => void;
  setBillingStatus: (status: BillingStatus | null) => void;
  setUser: (user: AuthUser | null) => void;
}

export const AuthContext = React.createContext<AuthContextValue | undefined>(undefined);

export function useAuth() {
  const ctx = React.useContext(AuthContext);
  if (!ctx) {
    throw new Error('useAuth must be used within an AuthContext.Provider');
  }
  return ctx;
}
